import { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck, Pencil, Trash2, X } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';

const PERMS = ['view', 'create', 'edit', 'delete', 'export'] as const;
type PermKey = typeof PERMS[number];

interface ModuleEntry { name: string }
type PermMap = Record<string, Partial<Record<PermKey, boolean>>>;

interface PlatformRole {
  id: string;
  name: string;
  description: string;
  permissions: PermMap;
  isPreset: boolean;
  isActive: boolean;
}

function buildEmpty(modules: ModuleEntry[]): PermMap {
  const p: PermMap = {};
  for (const m of modules) {
    p[m.name] = Object.fromEntries(PERMS.map((k) => [k, false])) as Record<PermKey, boolean>;
  }
  return p;
}

function mergePermissions(saved: any, modules: ModuleEntry[]): PermMap {
  const base = buildEmpty(modules);
  if (!saved || typeof saved !== 'object') return base;
  // Boolean-valued top-level keys = legacy flat shape; ignore here
  // (backend boot seed upgrades these in place).
  for (const m of modules) {
    const row = saved[m.name];
    if (row && typeof row === 'object') {
      for (const perm of PERMS) base[m.name][perm] = !!row[perm];
    }
  }
  // Preserve permissions for modules that may have been removed from
  // the catalog so admin sees the full saved state.
  for (const key of Object.keys(saved)) {
    if (base[key]) continue;
    const row = saved[key];
    if (row && typeof row === 'object') {
      base[key] = {};
      for (const perm of PERMS) base[key][perm] = !!row[perm];
    }
  }
  return base;
}

// ── Role Form Drawer ────────────────────────────────────────────────────────
function RoleDrawer({
  role, modules, onClose, onSaved,
}: {
  role?: PlatformRole;
  modules: ModuleEntry[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!role;
  const [form, setForm] = useState({
    name: role?.name ?? '',
    description: role?.description ?? '',
    isActive: role?.isActive ?? true,
    permissions: mergePermissions(role?.permissions, modules),
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (mod: string, perm: PermKey) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [mod]: { ...(prev.permissions[mod] || {}), [perm]: !prev.permissions[mod]?.[perm] },
      },
    }));
  };

  const toggleAllModule = (mod: string) => {
    const allOn = PERMS.every(p => form.permissions[mod]?.[p]);
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [mod]: Object.fromEntries(PERMS.map(p => [p, !allOn])) as Record<PermKey, boolean>,
      },
    }));
  };

  const toggleAllPerm = (perm: PermKey) => {
    const allOn = modules.every(m => form.permissions[m.name]?.[perm]);
    setForm(prev => {
      const next = { ...prev.permissions };
      for (const m of modules) {
        next[m.name] = { ...(next[m.name] || {}), [perm]: !allOn };
      }
      return { ...prev, permissions: next };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await saApi.patch(`/super-admin/roles/${role!.id}`, form);
      } else {
        await saApi.post('/super-admin/roles', form);
      }
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full sm:w-[680px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#1E293B]">{isEdit ? 'Edit Role' : 'New Role'}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#64748B] transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Role Name <span className="text-red-500">*</span></label>
            <input
              className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent"
              placeholder="e.g. Billing Manager"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              disabled={isEdit && role?.isPreset}
            />
            {isEdit && role?.isPreset && <p className="text-[11px] text-slate-400">Preset role names cannot be changed</p>}
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Description</label>
            <textarea
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent resize-none"
              placeholder="What can this role do?"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>

          {/* Permission grid */}
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <label className="block text-xs font-normal text-slate-500 uppercase tracking-wider">Module Permissions</label>
              <p className="text-[11px] text-slate-400">Add a new module from <span className="font-medium">Modules</span> page.</p>
            </div>
            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#6366F1] border-b-2 border-indigo-700">
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-white uppercase tracking-wider w-44">Module</th>
                    {PERMS.map(perm => (
                      <th key={perm} className="px-2 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => toggleAllPerm(perm)}
                          className="text-xs capitalize hover:text-indigo-100 transition-colors font-medium"
                          title={`Toggle all ${perm}`}
                        >
                          {perm}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.length === 0 ? (
                    <tr>
                      <td colSpan={PERMS.length + 1} className="px-4 py-6 text-center text-xs text-slate-400">
                        No platform modules configured. Add one from the Modules page first.
                      </td>
                    </tr>
                  ) : modules.map((mod, idx) => {
                    const row = form.permissions[mod.name] ?? {};
                    const allOn = PERMS.every(p => row[p]);
                    return (
                      <tr key={mod.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => toggleAllModule(mod.name)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors"
                            title={`Toggle all for ${mod.name}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${allOn ? 'bg-blue-500' : 'bg-slate-300'}`} />
                            <span className="font-medium text-slate-700 text-sm">{mod.name}</span>
                          </button>
                        </td>
                        {PERMS.map(perm => (
                          <td key={perm} className="px-2 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!row[perm]}
                              onChange={() => togglePerm(mod.name, perm)}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 cursor-pointer accent-blue-600"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">Click a column header to toggle every module, or a module name to toggle the row.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-[#64748B] border border-gray-200 hover:border-gray-300 transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#6366F1] hover:bg-[#4F46E5] text-white transition-colors disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Roles Page ──────────────────────────────────────────────────────────────
export function SuperAdminRolesPage() {
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [modules, setModules] = useState<ModuleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { sorted: sortedRoles, sort, onSort } = useSort(roles);
  const [drawer, setDrawer] = useState<{ open: boolean; role?: PlatformRole }>({ open: false });
  const [confirmRole, setConfirmRole] = useState<PlatformRole | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, modulesRes] = await Promise.all([
        saApi.get('/super-admin/roles'),
        saApi.get('/super-admin/roles/modules'),
      ]);
      const list: PlatformRole[] = Array.isArray(rolesRes.data)
        ? rolesRes.data
        : Array.isArray(rolesRes.data?.data)
          ? rolesRes.data.data
          : [];
      setRoles(list);
      setModules(Array.isArray(modulesRes.data) ? modulesRes.data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || err?.message || 'Failed to load roles');
      console.error('[SA Roles] fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = (role: PlatformRole) => {
    if (role.isPreset) { toast('error', 'Cannot delete preset roles'); return; }
    setConfirmRole(role);
  };

  const handleConfirmDelete = async () => {
    if (!confirmRole) return;
    setConfirmLoading(true);
    try {
      await saApi.delete(`/super-admin/roles/${confirmRole.id}`);
      setConfirmRole(null);
      fetchAll();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete role');
    } finally {
      setConfirmLoading(false);
    }
  };

  // Quick summary for the table: count of modules with view=true.
  const visibleModuleCount = (perms: PermMap) =>
    Object.values(perms || {}).filter((row) => row && row.view).length;

  return (
    <SuperAdminLayout title="Roles">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#64748B]">Platform roles control what super-admin team members can access. Permissions are driven by the modules in the catalog.</p>
          <button onClick={() => setDrawer({ open: true })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
            <ShieldCheck className="h-4 w-4" /> New Role
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <TableHeadRow>
                <SortTh label="Role" sortKey="name" sort={sort} onSort={onSort} className="px-5 py-3.5" />
                <SortTh label="Description" sortKey="description" sort={sort} onSort={onSort} className="px-5 py-3.5" />
                <SortTh label="Modules Visible" sortable={false} className="px-5 py-3.5 text-center whitespace-nowrap" />
                <SortTh label="Status" sortKey="isActive" sort={sort} onSort={onSort} className="px-5 py-3.5" align="center" />
                <SortTh label="Actions" sortable={false} className="px-5 py-3.5" />
              </TableHeadRow>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1] mx-auto" /></td></tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500">
                    No roles found. Use "New Role" to create one.
                  </td>
                </tr>
              ) : sortedRoles.map(role => (
                <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="h-4 w-4 text-[#6366F1]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E293B] flex items-center gap-1.5">
                          {role.name}
                          {role.isPreset && (
                            <span className="text-xs font-normal px-1.5 py-0.5 bg-gray-100 text-[#64748B] rounded">preset</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-[#64748B] text-sm max-w-sm">
                    <p className="truncate">{role.description || '—'}</p>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-indigo-50 text-[#6366F1] text-xs font-semibold">
                      {visibleModuleCount(role.permissions)} / {modules.length}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    {role.isActive ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase">Active</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase">Inactive</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDrawer({ open: true, role })}
                        className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#6366F1] hover:bg-indigo-50 transition-colors">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {!role.isPreset && (
                        <button onClick={() => handleDelete(role)}
                          className="p-1.5 rounded-md text-[#94A3B8] hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drawer.open && (
        <RoleDrawer
          role={drawer.role}
          modules={modules}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); fetchAll(); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmRole}
        title="Delete Role?"
        message={`Are you sure you want to delete the role "${confirmRole?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRole(null)}
        isLoading={confirmLoading}
      />
    </SuperAdminLayout>
  );
}
